// src/components/common/TrendTutorial.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import './TrendTutorial.css';

const TUTORIAL_STORAGE_KEY = 'trendmix_tutorial_completed';

const TrendTutorial = ({ shouldStart = false }) => {
  const { translate } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [arrowPosition, setArrowPosition] = useState('left');
  const tooltipRef = useRef(null);

  // Tutorial steps configuration - memoized to prevent re-renders
  const steps = useMemo(() => [
    {
      selector: '.sub-panel-view.visible > .filter-panel-content > div:nth-child(2)',
      title: translate('tutorial.step1.title') || 'Select Category',
      description: translate('tutorial.step1.description') || 'Start here by selecting a product category. Only one category can be selected at a time.',
      position: 'right',
      highlight: true
    },
    {
      selector: '.sub-panel-view.visible > .filter-panel-content > div:nth-child(3)',
      title: translate('tutorial.step2.title') || 'Add Filters',
      description: translate('tutorial.step2.description') || 'Click "Add Filter" to choose filter classes and options. You can select one option per class, up to 4 classes maximum.',
      position: 'right',
      highlight: true
    },
    {
      selector: '.apply-filters-button',
      title: translate('tutorial.step3.title') || 'Apply Filters',
      description: translate('tutorial.step3.description') || 'Click Apply to load the full data analysis report for beauty products based on your selected options.',
      position: 'top-right',
      highlight: true
    }
  ], [translate]);

  // Check if tutorial should be shown (only when shouldStart becomes true)
  useEffect(() => {
    if (!shouldStart) return;

    const tutorialCompleted = localStorage.getItem(TUTORIAL_STORAGE_KEY);
    if (!tutorialCompleted) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [shouldStart]);

  // Calculate tooltip position based on target element
  const updateTooltipPosition = useCallback(() => {
    const step = steps[currentStep];
    if (!step) return;

    const targetElement = document.querySelector(step.selector);
    if (!targetElement) return;

    const rect = targetElement.getBoundingClientRect();
    const tooltipWidth = 320;
    const tooltipHeight = 180;
    const padding = 16;

    let top, left, arrow;

    if (step.position === 'right') {
      // Position tooltip to the right of the element
      top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
      left = rect.right + padding;
      arrow = 'left';

      // Check if tooltip goes off screen right
      if (left + tooltipWidth > window.innerWidth - padding) {
        left = rect.left - tooltipWidth - padding;
        arrow = 'right';
      }
    } else if (step.position === 'left') {
      // Position tooltip to the left of the element
      top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
      left = rect.left - tooltipWidth - padding;
      arrow = 'right';

      // Check if tooltip goes off screen left
      if (left < padding) {
        left = rect.right + padding;
        arrow = 'left';
      }
    } else if (step.position === 'bottom') {
      // Position tooltip below the element
      top = rect.bottom + padding;
      left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
      arrow = 'top';
    } else if (step.position === 'top') {
      // Position tooltip above the element
      top = rect.top - tooltipHeight - padding;
      left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
      arrow = 'bottom';
    } else if (step.position === 'top-right') {
      // Position tooltip to the right, higher up with arrow at bottom-left
      top = rect.top - tooltipHeight - 30; // Higher position
      left = rect.right + padding; // Same horizontal as 'right' position
      arrow = 'left-bottom'; // Same arrow as step 1/2 but lower
    }

    // Keep tooltip within viewport
    top = Math.max(padding, Math.min(top, window.innerHeight - tooltipHeight - padding));
    left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding));

    setTooltipPosition({ top, left });
    setArrowPosition(arrow);

    // Add highlight to target element
    document.querySelectorAll('.tutorial-highlight').forEach(el => {
      el.classList.remove('tutorial-highlight');
    });
    if (step.highlight) {
      targetElement.classList.add('tutorial-highlight');
    }
  }, [currentStep, steps]);

  // Update position on step change or window resize
  useEffect(() => {
    if (!isVisible) return;

    updateTooltipPosition();

    const handleResize = () => updateTooltipPosition();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
      // Clean up highlights
      document.querySelectorAll('.tutorial-highlight').forEach(el => {
        el.classList.remove('tutorial-highlight');
      });
    };
  }, [isVisible, updateTooltipPosition]);

  const handleComplete = useCallback(() => {
    localStorage.setItem(TUTORIAL_STORAGE_KEY, 'true');
    setIsVisible(false);
    // Clean up highlights
    document.querySelectorAll('.tutorial-highlight').forEach(el => {
      el.classList.remove('tutorial-highlight');
    });
  }, []);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  }, [currentStep, steps.length, handleComplete]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const handleSkip = useCallback(() => {
    handleComplete();
  }, [handleComplete]);

  // Don't render if not visible
  if (!isVisible) return null;

  const step = steps[currentStep];

  return (
    <>
      {/* Overlay */}
      <div className="tutorial-overlay" onClick={handleSkip} />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`tutorial-tooltip tutorial-arrow-${arrowPosition}`}
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left
        }}
      >
        {/* Header */}
        <div className="tutorial-header">
          <div className="tutorial-step-indicator">
            {translate('tutorial.step') || 'Step'} {currentStep + 1} / {steps.length}
          </div>
          <button className="tutorial-close" onClick={handleSkip} aria-label="Close tutorial">
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="tutorial-content">
          <h3 className="tutorial-title">{step.title}</h3>
          <p className="tutorial-description">{step.description}</p>
        </div>

        {/* Footer */}
        <div className="tutorial-footer">
          <button className="tutorial-skip" onClick={handleSkip}>
            {translate('tutorial.skip') || 'Skip Tutorial'}
          </button>
          <div className="tutorial-nav">
            {currentStep > 0 && (
              <button className="tutorial-btn tutorial-btn-secondary" onClick={handlePrev}>
                <ChevronLeft size={16} />
                {translate('tutorial.prev') || 'Back'}
              </button>
            )}
            <button className="tutorial-btn tutorial-btn-primary" onClick={handleNext}>
              {currentStep === steps.length - 1
                ? (translate('tutorial.finish') || 'Got it!')
                : (translate('tutorial.next') || 'Next')
              }
              {currentStep < steps.length - 1 && (
                <ChevronRight size={16} />
              )}
            </button>
          </div>
        </div>

        {/* Progress dots */}
        <div className="tutorial-progress">
          {steps.map((_, index) => (
            <span
              key={index}
              className={`tutorial-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default TrendTutorial;
